<template>
    <v-card :elevation="1" class="background-2 round-card" width="100%">
        <v-card-title class="pa-3 py-1">
            <div class="pl-2 pl-xl-3 subtitle-2">
                Configuratore Card Mobile
            </div>
            <v-spacer></v-spacer>
            <span class="caption mr-2">
                {{ usedFieldIds.size }} campi attivi
            </span>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text v-if="!isLoaded" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
            <div class="caption grey--text mt-2">Caricamento configurazione…</div>
        </v-card-text>

        <v-card-text v-else class="card-config-wrap pa-3">
            <div class="card-config">

                <!-- LEFT: PREVIEW -->
                <div class="cfg-col cfg-col-preview">
                    <div class="col-label">
                        <span class="dot"></span>
                        Anteprima live
                    </div>

                    <div class="preview-pane">
                        <div class="preview-pane-title">Anteprima Card Mobile</div>
                        <div class="preview-sub">
                            Valori di esempio: così appare la card in app.
                        </div>

                        <div class="preview-stage">
                            <!-- Card Vuetify mock — allineata alle primitive mobile -->
                            <v-card class="preview-m-card" outlined elevation="1">
                                <!-- Bordo sinistro colorato — reactive al border admin override / chrome.border default -->
                                <div class="preview-color-bar" :style="{ background: previewBorderColor }"></div>

                                <!-- Slot foto (placeholder, NON renderizza la foto reale): la card
                                     prodotto porta UNA foto su mobile (T4). Mostrato solo se il backend
                                     dichiara media.photo — le altre entità non hanno questo riquadro. -->
                                <div v-if="cardMedia && cardMedia.photo" class="preview-photo-ph">
                                    <v-icon size="20" color="grey lighten-1">mdi-image-outline</v-icon>
                                    <span class="preview-photo-ph-label">Foto</span>
                                </div>

                                <div class="preview-card-inner">
                                    <!-- Chrome header (da chrome backend o null) -->
                                    <div v-if="chrome" class="preview-header">
                                        <div class="preview-header-left">
                                            <!-- Identifier chip (numero ticket) -->
                                            <v-chip
                                                v-if="chrome.identifier && chromeValue('identifier')"
                                                x-small
                                                color="grey darken-2"
                                                dark
                                                class="font-weight-bold mr-1 preview-chip-mono">
                                                #{{ chromeValue('identifier') }}
                                            </v-chip>
                                            <!-- Status chip colorato — reactive a chromeStatusKey override -->
                                            <v-chip
                                                v-if="effectiveStatusKey && previewStatusValue"
                                                x-small
                                                :color="previewStatusColor"
                                                dark
                                                class="font-weight-bold preview-status-chip">
                                                {{ previewStatusValue }}
                                            </v-chip>
                                        </div>
                                        <div class="preview-header-right">
                                            <!-- Meta right (data) -->
                                            <span v-if="chrome.meta_right && chromeValue('meta_right')" class="preview-meta-right">
                                                <v-icon size="12" color="grey darken-1" class="mr-1">mdi-calendar-outline</v-icon>
                                                {{ chromeValue('meta_right') }}
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Body — hero senza label (titolo → entità collegata → assegnatari)
                                         + griglia di celle per i campi secondari. Il ruolo di ogni campo
                                         lo decide bodyRoles, mirror di cardLayout.js del mobile.
                                         previewBodySlots filtra i campi già nel chrome (no duplica). -->
                                    <div v-if="previewBodySlots.length" class="preview-body">
                                        <template v-for="fid in previewHeroSlots">
                                            <div
                                                v-if="bodyVariant(fid) === 'title'"
                                                :key="'h-title-' + fid"
                                                class="preview-title">{{ sampleValue(fid) }}</div>

                                            <div
                                                v-else-if="bodyVariant(fid) === 'entity'"
                                                :key="'h-entity-' + fid"
                                                class="preview-entity">
                                                <span v-if="monogram(sampleValue(fid))" class="preview-entity-mono">{{ monogram(sampleValue(fid)) }}</span>
                                                <span class="preview-entity-text">
                                                    <span class="preview-entity-name">{{ sampleValue(fid) }}</span>
                                                    <span class="preview-entity-caption">{{ fieldLabel(fid) }}</span>
                                                </span>
                                            </div>

                                            <div v-else :key="'h-assign-' + fid" class="preview-assignees">
                                                <v-icon size="13" color="grey darken-1">mdi-account-multiple</v-icon>
                                                <span class="preview-assignees-value">{{ sampleValue(fid) }}</span>
                                            </div>
                                        </template>

                                        <div v-if="previewGridSlots.length" class="preview-grid">
                                            <div
                                                v-for="fid in previewGridSlots"
                                                :key="'g-' + fid"
                                                class="preview-cell"
                                                :class="{ 'preview-cell--wide': bodyVariant(fid) === 'wide' }">
                                                <span class="preview-cell-label">{{ fieldLabel(fid) }}</span>
                                                <span class="preview-cell-value">{{ sampleValue(fid) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="preview-body preview-body-empty">
                                        <span class="caption grey--text">Nessun campo nel body</span>
                                    </div>

                                    <!-- Espansione preview: collassata di default, toggle col chevron — replica EntityListCard mobile -->
                                    <div v-if="previewExpandedSlots.length" class="preview-expansion">
                                        <v-btn x-small text color="grey darken-2" class="pa-0 mt-1" @click.stop="previewExpanded = !previewExpanded">
                                            <v-icon size="14" class="mr-1" :class="{'preview-chevron-open': previewExpanded}">mdi-chevron-down</v-icon>
                                            {{ previewExpandedSlots.length }} {{ previewExpandedSlots.length === 1 ? 'campo espanso' : 'campi espansi' }}
                                        </v-btn>
                                        <!-- Stesse celle del body: un solo vocabolario aperto e chiuso -->
                                        <div v-if="previewExpanded" class="preview-grid preview-expanded-content">
                                            <div
                                                v-for="fid in previewExpandedSlots"
                                                :key="'exp-' + fid"
                                                class="preview-cell"
                                                :class="{ 'preview-cell--wide': bodyVariant(fid) === 'wide' }">
                                                <span class="preview-cell-label">{{ fieldLabel(fid) }}</span>
                                                <span class="preview-cell-value">{{ sampleValue(fid) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </v-card>
                        </div>

                    </div>
                </div>

                <!-- MIDDLE: SLOTS -->
                <div class="cfg-col cfg-col-slots">
                    <div class="col-label">
                        <span class="dot blue"></span>
                        Slot della card · trascina qui i campi
                    </div>
                    <div class="slots-column">

                        <!-- Chrome selectors: chip status + border colorato -->
                        <div class="chrome-selectors">
                            <div class="chrome-selector">
                                <label class="chrome-selector-label">Chip stato</label>
                                <v-select
                                    v-model="chromeStatusKey"
                                    :items="availableStatusOptions"
                                    item-text="text"
                                    item-value="value"
                                    dense outlined hide-details
                                    class="chrome-select"
						            :menu-props="{'offset-y': true}"
                                ></v-select>
                            </div>
                            <div class="chrome-selector">
                                <label class="chrome-selector-label">Border colorato</label>
                                <v-select
                                    v-model="chromeBorderKey"
                                    :items="availableBorderOptions"
                                    item-text="text"
                                    item-value="value"
                                    dense outlined hide-details
                                    class="chrome-select"
						            :menu-props="{'offset-y': true}"
                                ></v-select>
                            </div>
                        </div>

                        <!-- Body slot -->
                        <div class="slot-card">
                            <div class="slot-card-header">
                                <div class="slot-name">
                                    Body
                                    <span class="chip">body</span>
                                </div>
                                <div class="slot-meta">
                                    {{ slots.body.length }} {{ slots.body.length === 1 ? 'campo' : 'campi' }}
                                </div>
                            </div>
                            <draggable
                                v-model="slots.body"
                                :group="{ name: 'cardSlots', pull: true, put: true }"
                                :animation="150"
                                ghost-class="ghost-element"
                                chosen-class="chosen-element"
                                :class="['slot-list', !slots.body.length ? 'empty' : '']"
                                @add="onSlotAdd('body', $event)">
                                <div v-for="fid in slots.body" :key="fid" class="field-row">
                                    <span class="grip">⋮⋮</span>
                                    <span class="field-name">{{ fieldLabel(fid) }}</span>
                                    <span class="field-type">{{ fieldType(fid) }}</span>
                                    <div class="field-actions">
                                        <v-btn icon x-small color="error" @click="removeFromSlot('body', fid)">
                                            <v-icon size="14">mdi-close</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                                <div v-if="!slots.body.length" class="slot-empty-hint">
                                    Trascina qui titolo, sottotitolo, descrizione, meta…
                                </div>
                            </draggable>
                            <!-- Soft warning: body affollato (>5 campi) -->
                            <div v-if="slots.body.length > 5" class="slot-warning slot-warning-body">
                                <v-icon size="14" color="warning">mdi-alert-outline</v-icon>
                                Card affollata, consigliati max 5 campi. Salvataggio comunque permesso.
                            </div>
                        </div>

                        <!-- Espansione slot -->
                        <div class="slot-card">
                            <div class="slot-card-header">
                                <div class="slot-name">
                                    Espansione
                                    <span class="chip">expanded</span>
                                </div>
                                <div class="slot-meta">
                                    {{ slots.expanded.length }} {{ slots.expanded.length === 1 ? 'campo' : 'campi' }}
                                </div>
                            </div>
                            <draggable
                                v-model="slots.expanded"
                                :group="{ name: 'cardSlots', pull: true, put: true }"
                                :animation="150"
                                ghost-class="ghost-element"
                                chosen-class="chosen-element"
                                :class="['slot-list', !slots.expanded.length ? 'empty' : '']"
                                @add="onSlotAdd('expanded', $event)">
                                <div v-for="fid in slots.expanded" :key="fid" class="field-row">
                                    <span class="grip">⋮⋮</span>
                                    <span class="field-name">{{ fieldLabel(fid) }}</span>
                                    <span class="field-type">{{ fieldType(fid) }}</span>
                                    <div class="field-actions">
                                        <v-btn icon x-small color="error" @click="removeFromSlot('expanded', fid)">
                                            <v-icon size="14">mdi-close</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                                <div v-if="!slots.expanded.length" class="slot-empty-hint">
                                    Campi mostrati al tap su "espandi" — contratto, asset, scadenza…
                                </div>
                            </draggable>
                        </div>
                    </div>
                </div>

                <!-- RIGHT: AVAILABLE FIELDS -->
                <div class="cfg-col cfg-col-available">
                    <div class="col-label">
                        <span class="dot green"></span>
                        Campi disponibili · sorgente del drag
                    </div>
                    <div class="available-column">
                        <div class="available-header">
                            <span class="title">Campi Disponibili</span>
                            <span class="count">{{ usedFieldIds.size }}/{{ allFields.length }}</span>
                        </div>
                        <div class="available-search">
                            <v-icon size="13" class="search-icon">mdi-magnify</v-icon>
                            <input
                                type="text"
                                placeholder="Cerca campo…"
                                :value="searchQuery"
                                @input="onSearchInput($event.target.value)" />
                        </div>
                        <div class="available-list">
                            <div v-if="!filteredAvailable.length" class="text-center grey--text caption py-4">
                                Nessun campo disponibile
                            </div>
                            <draggable
                                v-else
                                :list="filteredAvailable"
                                :group="{ name: 'cardSlots', pull: 'clone', put: false }"
                                :sort="false"
                                :clone="cloneAvailable"
                                :move="onAvailableMove">
                                <div
                                    v-for="f in filteredAvailable"
                                    :key="f.id"
                                    :class="['field-row', usedFieldIds.has(f.id) ? 'used' : '']"
                                    :title="usedFieldIds.has(f.id) ? 'Già utilizzato' : 'Trascina nello slot'">
                                    <span class="grip">⋮⋮</span>
                                    <span class="field-name">{{ f.label }}</span>
                                    <span class="field-type">{{ f.type || '—' }}</span>
                                    <span v-if="usedFieldIds.has(f.id)" class="check">
                                        <v-icon size="14" color="success">mdi-check</v-icon>
                                    </span>
                                    <div v-else class="field-actions">
                                        <v-btn icon x-small color="primary" @click="quickAdd(f.id)">
                                            <v-icon size="14">mdi-plus</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </draggable>
                        </div>
                    </div>
                </div>
            </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
            <!-- Torna ai campi scelti dallo sviluppatore: senza, una prova salvata
                 non si poteva piu' annullare. Applica agli slot, si conferma con Salva. -->
            <v-btn v-if="defaults" text :disabled="!isLoaded || saving" @click="restoreDefaults">
                Ripristina predefiniti
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" :loading="saving" :disabled="!isLoaded" @click="save">
                Salva configurazione
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import draggable from 'vuedraggable'

// Tipi consentiti nel body — no file/editor.
// `number/time/duration` aggiunti per pool Intervention (id, start/end_time, duration_minutes).
const BODY_ALLOWED_TYPES = new Set([
    'text', 'textarea', 'longtext', 'relation', 'date', 'datetime', 'time',
    'duration', 'number', 'currency', 'boolean', 'general_type',
])

// Mappa colore CONDITION (advanced type, NON gestito dai general_types backend).
// Solo questa è hardcoded; status e priority leggono da info.general_types.
const CONDITION_COLOR_HEX = {
    'active': '#43A047',
    'expiring': '#FB8C00',
    'expired': '#E53935',
    'closed': '#9E9E9E',
}

// Date format helper — DD/MM/YYYY
function formatDateIT(iso) {
    if (!iso) return ''
    try {
        const d = new Date(iso)
        if (isNaN(d.getTime())) return iso
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        return `${dd}/${mm}/${d.getFullYear()}`
    } catch (e) { return iso }
}

export default {
    name: 'entity-mobile-card-configurator',
    components: { draggable },

    props: {
        model: {
            type: String,
            default: 'trouble',
        },
        // Rotta backend: {entity}-info con ?card=1. Le vecchie `*-mobile-info`
        // stavano fuori dal gruppo `permissions` solo per come CheckPermissions
        // interpretava il nome, e non esistono piu'.
        infoUrl: {
            type: String,
            default: 'troubles-info?card=1',
        },
    },

    data() {
        return {
            isLoaded: false,
            saving: false,
            allFields: [],
            // Schema slim: solo body + expanded (array di field-key string)
            slots: {
                body: [],
                expanded: [],
            },
            chrome: null, // { identifier:{key,type}, status:{key,type}, meta_right:{key,type}, border?:{key,type} } | null
            cardMedia: null, // { photo:bool } | null — hint backend per slot foto (es. product)
            // Default dello sviluppatore ({body, expanded}); null su backend che non li espone
            defaults: null,
            // Admin override chrome: null = "Predefinito" (chrome statico)
            chromeStatusKey: null,
            chromeBorderKey: null,
            // Candidati chrome dal backend: { status:[keys], border:[keys] }
            chromeCandidates: { status: [], border: [] },
            // general_types per testo e colore di esempio dei campi a scelta (dal blocco info di -info)
            generalTypes: {},
            previewExpanded: false,
            searchQuery: '',
            searchDebounceTimer: null,
        }
    },

    computed: {
        fieldsById() {
            const map = {}
            this.allFields.forEach(f => { map[f.id] = f })
            return map
        },
        usedFieldIds() {
            const s = new Set()
            this.slots.body.forEach(id => s.add(id))
            this.slots.expanded.forEach(id => s.add(id))
            // Chrome statico (developer-curated): identifier + meta_right sempre nascosti dal pool
            if (this.chrome) {
                if (this.chrome.identifier && this.chrome.identifier.key) s.add(this.chrome.identifier.key)
                if (this.chrome.meta_right && this.chrome.meta_right.key) s.add(this.chrome.meta_right.key)
            }
            // Chrome admin override: status_key + border_key (se selezionati) nascosti dal pool
            if (this.chromeStatusKey) s.add(this.chromeStatusKey)
            if (this.chromeBorderKey) s.add(this.chromeBorderKey)
            return s
        },
        // Default key letti dal chrome statico esposto dal backend.
        // Sono i fallback usati quando admin sceglie "Predefinito".
        defaultStatusKey() {
            return (this.chrome && this.chrome.status && this.chrome.status.key) || null
        },
        defaultBorderKey() {
            return (this.chrome && this.chrome.border && this.chrome.border.key) || null
        },
        availableStatusOptions() {
            const defaultLabel = this.defaultStatusKey
                ? `Predefinito (${this.fieldLabel(this.defaultStatusKey)})`
                : 'Predefinito'
            const opts = [{ value: null, text: defaultLabel }]
            const keys = Array.isArray(this.chromeCandidates.status) ? this.chromeCandidates.status : []
            keys.forEach(k => {
                opts.push({ value: k, text: this.fieldLabel(k) })
            })
            return opts
        },
        availableBorderOptions() {
            const defaultLabel = this.defaultBorderKey
                ? `Predefinito (${this.fieldLabel(this.defaultBorderKey)})`
                : 'Predefinito'
            const opts = [{ value: null, text: defaultLabel }]
            const keys = Array.isArray(this.chromeCandidates.border) ? this.chromeCandidates.border : []
            keys.forEach(k => {
                opts.push({ value: k, text: this.fieldLabel(k) })
            })
            return opts
        },
        // Effective = override admin se valorizzato, altrimenti default dal chrome statico.
        effectiveStatusKey() {
            return this.chromeStatusKey || this.defaultStatusKey
        },
        effectiveBorderKey() {
            return this.chromeBorderKey || this.defaultBorderKey
        },
        // Valore + colore del chip status nella preview (reactive ai select admin).
        previewStatusValue() {
            return this.effectiveStatusKey ? this.sampleValue(this.effectiveStatusKey) : ''
        },
        previewStatusColor() {
            return this.sampleColor(this.effectiveStatusKey) || '#FFB300'
        },
        // Colore del border bar nella preview (reactive al select admin / chrome.border default).
        previewBorderColor() {
            return this.sampleColor(this.effectiveBorderKey) || '#3b82f6'
        },
        filteredAvailable() {
            const q = (this.searchQuery || '').trim().toLowerCase()
            const list = this.allFields.filter(f => !this.usedFieldIds.has(f.id))
            if (!q) return list
            return list.filter(f =>
                (f.label || '').toLowerCase().includes(q) ||
                (f.id || '').toLowerCase().includes(q),
            )
        },
        // Skip-set per evitare di renderizzare nei body/expanded slot quei field
        // che sono GIÀ nel chrome (identifier/status/meta_right).
        // Allineato a EntityListCard mobile: stessa logica di de-duplica.
        previewSkipKeys() {
            const s = new Set()
            if (this.chrome?.identifier?.key) s.add(this.chrome.identifier.key)
            if (this.chrome?.meta_right?.key) s.add(this.chrome.meta_right.key)
            if (this.effectiveStatusKey) s.add(this.effectiveStatusKey)
            return s
        },
        previewBodySlots() {
            return (this.slots.body || []).filter(fid => !this.previewSkipKeys.has(fid))
        },
        previewExpandedSlots() {
            return (this.slots.expanded || []).filter(fid => !this.previewSkipKeys.has(fid))
        },

        // Ruolo di layout per ogni campo del body — mirror di classifyBodyFields
        // (v. commento in methods). Mappa fid → title|entity|assignees|wide|cell.
        bodyRoles() {
            const ids = this.previewBodySlots
            const isTextLike = (t) => !t || t === 'text' || t === 'string' || t === 'textarea' || t === 'multiline'

            const titleId = ids.find(id => {
                const f = this.fieldsById[id]
                return f && !this.isAssigneeField(id) && isTextLike(f.type)
            }) || ids.find(id => {
                const f = this.fieldsById[id]
                return f && !this.isAssigneeField(id) && f.type === 'relation'
            })

            const entityId = ids.find(id =>
                id !== titleId && !this.isAssigneeField(id) && this.isEntityField(id)
            )

            const roles = {}
            ids.forEach(id => {
                const f = this.fieldsById[id]
                if (this.isAssigneeField(id)) roles[id] = 'assignees'
                else if (id === titleId) roles[id] = 'title'
                else if (id === entityId) roles[id] = 'entity'
                else if (f && f.type === 'longtext') roles[id] = 'wide'
                else roles[id] = 'cell'
            })
            return roles
        },

        // L'hero tiene al massimo titolo + entita' collegata + assegnatari.
        // Un record senza assegnatari non disegna il blocco (come sul mobile),
        // ma il RUOLO della colonna non cambia.
        previewHeroSlots() {
            return ['title', 'entity', 'assignees']
                .map(role => this.previewBodySlots.find(id => this.bodyRoles[id] === role))
                .filter(id => id !== undefined && this.sampleValue(id) !== '')
        },
        previewGridSlots() {
            return this.previewBodySlots.filter(id => ['wide', 'cell'].indexOf(this.bodyRoles[id]) !== -1)
        },
    },

    methods: {
        async load() {
            try {
                // Il blocco card viaggia dentro {entity}-info sotto la chiave `card`,
                // e ci arriva solo grazie a ?card=1: questo configuratore gira su
                // browser desktop e non manda l'header device che basta al mobile.
                const { data: payload } = await this.axios.get(this.infoUrl)
                const data = payload && payload.card
                if (!data) {
                    // Meglio l'errore che un configuratore vuoto senza spiegazione.
                    throw new Error(`Risposta senza blocco card da ${this.infoUrl}: manca ?card=1?`)
                }

                this.allFields = (data.fieldDefs || [])
                    .filter(f => f.key && f.key !== 'actions')
                    .map(f => ({
                        id: f.key,
                        key: f.key,
                        value: f.value,
                        type: f.type || null,
                        subtype: f.subtype || null,
                        text_value: f.text_value || null,
                        subkey: f.subkey || null,
                        label: this.$trans ? this.$trans(f.text) : f.text,
                    }))

                // general_types dalla stessa risposta ({info, card}): danno testo e colore
                // di esempio a stato, priorità e simili
                this.generalTypes = (payload.info && payload.info.general_types) || {}

                const d = data.defaults
                this.defaults = (d && Array.isArray(d.body) && Array.isArray(d.expanded)) ? d : null

                // Carica chrome dal nuovo payload
                this.chrome = (data.chrome && typeof data.chrome === 'object') ? data.chrome : null

                // Hint media (slot foto): solo le entità che lo dichiarano (es.
                // product) mostrano il riquadro foto placeholder nell'anteprima.
                this.cardMedia = (data.media && typeof data.media === 'object') ? data.media : null

                // Carica candidati chrome dal backend (graceful: vecchio backend → array vuoti)
                const cands = data.chrome_candidates
                this.chromeCandidates = {
                    status: (cands && Array.isArray(cands.status)) ? cands.status : [],
                    border: (cands && Array.isArray(cands.border)) ? cands.border : [],
                }

                // Carica config — schema slim {body:[], expanded:[], chrome?:{status_key?, border_key?}}
                // Se shape vecchia (header/footer/colorBy/...) → tratta come null
                const cfg = data.card_configuration
                if (cfg && Array.isArray(cfg.body) && Array.isArray(cfg.expanded)) {
                    const validKeys = new Set(this.allFields.map(f => f.id))
                    const valid = (arr) => arr
                        .filter(item => typeof item === 'string' && validKeys.has(item))
                    this.slots = {
                        body: valid(cfg.body),
                        expanded: valid(cfg.expanded),
                    }
                    // Carica admin override chrome (validato contro candidati)
                    const adminChrome = (cfg.chrome && typeof cfg.chrome === 'object') ? cfg.chrome : null
                    this.chromeStatusKey = (adminChrome && this.chromeCandidates.status.includes(adminChrome.status_key))
                        ? adminChrome.status_key
                        : null
                    this.chromeBorderKey = (adminChrome && this.chromeCandidates.border.includes(adminChrome.border_key))
                        ? adminChrome.border_key
                        : null
                } else {
                    // Nessuna config o shape legacy → slot vuoti, no override chrome
                    this.slots = { body: [], expanded: [] }
                    this.chromeStatusKey = null
                    this.chromeBorderKey = null
                }

                this.isLoaded = true
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('EntityMobileCardConfigurator: load error', err)
                this.$store.commit('setSnackbar', {
                    timeout: 6000,
                    text: 'Errore nel caricamento della configurazione card mobile',
                    value: true,
                    color: 'error',
                })
            }
        },

        // Prima opzione dei general_types del campo: dà testo e colore di esempio
        firstGeneralType(fieldDef) {
            const options = this.generalTypes[fieldDef.key]
            if (!options || typeof options !== 'object') return null
            const first = Object.values(options)[0]
            return first && typeof first === 'object' ? first : null
        },

        sampleColor(fid) {
            const f = this.fieldsById[fid]
            if (!f) return null
            if (f.type === 'general_type') {
                const option = this.firstGeneralType(f)
                return (option && option.extra_fields && option.extra_fields.color) || null
            }
            if (f.type === 'advanced' && f.key === 'trouble_condition') {
                return CONDITION_COLOR_HEX.active
            }
            return null
        },

        fieldLabel(fid) {
            const f = this.fieldsById[fid]
            return f ? f.label : fid
        },
        fieldType(fid) {
            const f = this.fieldsById[fid]
            return f ? (f.type || '—') : '—'
        },
        /**
         * Input:  fid del pool
         * Fa:     valore di esempio per l'anteprima, dal tipo e dal ruolo del campo:
         *         la card si vede uguale qualunque sia il primo record del cliente
         * Output: string
         */
        sampleValue(fid) {
            const f = this.fieldsById[fid]
            if (!f) return ''
            if (this.isAssigneeField(fid)) return 'Mario Rossi'
            const today = new Date()
            switch (f.type) {
                case 'general_type': {
                    const option = this.firstGeneralType(f)
                    return (option && option.text) || f.label
                }
                case 'date': return formatDateIT(today)
                case 'datetime': return formatDateIT(today) + ' 09:30'
                case 'time': return '09:30'
                case 'duration': return '1h 30m'
                case 'currency': return '€ 120,00'
                case 'number': return '12'
                case 'boolean': return 'Sì'
                case 'longtext':
                case 'textarea':
                    return 'Testo di esempio su più righe, come appare quando il campo è compilato.'
            }
            if (this.isEntityField(fid)) return 'Rossi Impianti S.r.l.'
            return `${f.label} di esempio`
        },

        // Valore di esempio del chrome (identifier/meta_right): i loro campi possono stare fuori dal pool.
        chromeValue(role) {
            if (!this.chrome || !this.chrome[role]) return ''
            if (role === 'identifier') return '1024'
            const key = this.chrome[role].key
            return this.sampleValue(key) || (this.chrome[role].type === 'date' ? formatDateIT(new Date()) : '')
        },

        // ── MIRROR di Hive_Mobile_Frontend/src/core/utils/cardLayout.js ──
        // La card mobile classifica ogni campo del body in un RUOLO di layout
        // (title | entity | assignees | wide | cell) e disegna una zona "hero"
        // senza label piu' una griglia di celle. Qui le stesse regole, riscritte
        // per Vue 2 perche' quel modulo non e' importabile da questo repo.
        // Se cambi la classificazione, cambiala in ENTRAMBI i posti — la deriva
        // fra preview e app e' gia' costata una volta.

        // Colonna assegnatari: si riconosce dal solo fieldDef, MAI dai dati della
        // riga — il ruolo di una colonna dev'essere lo stesso su tutte le card.
        isAssigneeField(fid) {
            const f = this.fieldsById[fid]
            if (!f) return false
            return /user|assegn|operator|tecnic|technician/.test(`${f.key || ''} ${f.value || ''}`.toLowerCase())
        },

        // Colonna entità collegata: `type: relation` oppure nome che la tradisce.
        // Nel pool dei ticket "Azienda" arriva SENZA type.
        isEntityField(fid) {
            const f = this.fieldsById[fid]
            if (!f) return false
            if (f.type === 'relation') return true
            return /company|azienda|cliente|customer|business|supplier|fornitore/.test(`${f.key || ''} ${f.value || ''}`.toLowerCase())
        },

        bodyVariant(fid) {
            return this.bodyRoles[fid] || 'cell'
        },

        // Iniziali per il monogramma dell'entita' collegata. Scarta le forme
        // societarie: "RS" per "Rossi S.r.l." e' meno riconoscibile di "RO".
        monogram(value) {
            if (value === null || value === undefined) return ''
            const legal = /^(s\.?r\.?l\.?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?|s\.?s\.?|scarl|srls|ss|coop|societa'?|di|e|&)$/i
            const tokens = String(value)
                .split(/[\s.,\-_/]+/)
                .filter(t => t && /^[^\W\d_]/.test(t) && !legal.test(t))
            if (tokens.length === 0) return ''
            if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase()
            return (tokens[0][0] + tokens[1][0]).toUpperCase()
        },

        // Vincoli UX body: hard-block per tipi non consentiti e secondo longtext.
        // Ritorna true se il drop è consentito, false con toast se bloccato.
        checkBodyDrop(fid) {
            const f = this.fieldsById[fid]
            const type = f ? f.type : null

            // Hard block: tipo non supportato
            if (type && !BODY_ALLOWED_TYPES.has(type)) {
                this.$store.commit('setSnackbar', {
                    timeout: 4000,
                    text: 'Tipo non supportato nella card',
                    value: true,
                    color: 'warning',
                })
                return false
            }

            // Hard block: secondo longtext nel body
            if (type === 'longtext') {
                const existingLongtext = this.slots.body.filter(id => {
                    const ff = this.fieldsById[id]
                    return ff && ff.type === 'longtext' && id !== fid
                })
                if (existingLongtext.length >= 1) {
                    this.$store.commit('setSnackbar', {
                        timeout: 4000,
                        text: 'Massimo 1 campo testo lungo nel body',
                        value: true,
                        color: 'warning',
                    })
                    return false
                }
            }

            return true
        },

        // Quando draggable inserisce un field via add(), sterilizza duplicati e rimuove dagli altri slot.
        onSlotAdd(targetSlot, evt) {
            // Recupera il fid appena aggiunto (ultimo elemento droppato)
            // Necessario per applicare vincoli specifici del body
            if (targetSlot === 'body') {
                // Applica vincoli: rimuovi eventuali violazioni introdotte dal drop
                const newBody = []
                let longtextCount = 0
                let blocked = false

                for (const fid of this.slots.body) {
                    const f = this.fieldsById[fid]
                    const type = f ? f.type : null

                    // Tipo non supportato → rimuovi silenziosamente e segnala
                    if (type && !BODY_ALLOWED_TYPES.has(type)) {
                        if (!blocked) {
                            this.$store.commit('setSnackbar', {
                                timeout: 4000,
                                text: 'Tipo non supportato nella card',
                                value: true,
                                color: 'warning',
                            })
                            blocked = true
                        }
                        continue
                    }

                    // Secondo longtext → rifiuta
                    if (type === 'longtext') {
                        longtextCount++
                        if (longtextCount > 1) {
                            if (!blocked) {
                                this.$store.commit('setSnackbar', {
                                    timeout: 4000,
                                    text: 'Massimo 1 campo testo lungo nel body',
                                    value: true,
                                    color: 'warning',
                                })
                                blocked = true
                            }
                            continue
                        }
                    }

                    newBody.push(fid)
                }
                this.slots.body = newBody
            }

            // Dedup target
            const seen = new Set()
            this.slots[targetSlot] = this.slots[targetSlot].filter(id => {
                if (seen.has(id)) return false
                seen.add(id)
                return true
            })

            // Rimuovi dai altri slot
            const targetIds = new Set(this.slots[targetSlot])
            const slotNames = ['body', 'expanded']
            slotNames.forEach(s => {
                if (s === targetSlot) return
                this.slots[s] = this.slots[s].filter(id => !targetIds.has(id))
            })
        },

        cloneAvailable(field) {
            return field.id
        },

        onAvailableMove(evt) {
            const fid = evt && evt.draggedContext && evt.draggedContext.element
                ? evt.draggedContext.element.id
                : null
            if (fid && this.usedFieldIds.has(fid)) return false
            return true
        },

        quickAdd(fieldId) {
            if (this.usedFieldIds.has(fieldId)) return
            // Applica vincoli body anche per quickAdd
            const f = this.fieldsById[fieldId]
            const type = f ? f.type : null
            if (type && !BODY_ALLOWED_TYPES.has(type)) {
                this.$store.commit('setSnackbar', {
                    timeout: 4000,
                    text: 'Tipo non supportato nella card',
                    value: true,
                    color: 'warning',
                })
                return
            }
            if (type === 'longtext') {
                const hasLongtext = this.slots.body.some(id => {
                    const ff = this.fieldsById[id]
                    return ff && ff.type === 'longtext'
                })
                if (hasLongtext) {
                    this.$store.commit('setSnackbar', {
                        timeout: 4000,
                        text: 'Massimo 1 campo testo lungo nel body',
                        value: true,
                        color: 'warning',
                    })
                    return
                }
            }
            this.slots.body.push(fieldId)
        },

        removeFromSlot(slot, fieldId) {
            this.slots[slot] = this.slots[slot].filter(id => id !== fieldId)
        },

        onSearchInput(value) {
            if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
            this.searchDebounceTimer = setTimeout(() => {
                this.searchQuery = value
            }, 200)
        },

        restoreDefaults() {
            if (!this.defaults) return
            const validKeys = new Set(this.allFields.map(f => f.id))
            this.slots = {
                body: this.defaults.body.filter(k => validKeys.has(k)),
                expanded: this.defaults.expanded.filter(k => validKeys.has(k)),
            }
            this.chromeStatusKey = null
            this.chromeBorderKey = null
            this.$store.commit('setSnackbar', {
                timeout: 5000,
                text: 'Predefiniti ripristinati: premi "Salva configurazione" per applicarli',
                value: true,
                color: 'info',
            })
        },

        async save() {
            this.saving = true
            try {
                // Schema slim+chrome: body + expanded + chrome opzionale
                const value = {
                    body: [...this.slots.body],
                    expanded: [...this.slots.expanded],
                }
                // Includi chrome solo se l'admin ha selezionato almeno una key (≠ Predefinito)
                if (this.chromeStatusKey || this.chromeBorderKey) {
                    value.chrome = {}
                    if (this.chromeStatusKey) value.chrome.status_key = this.chromeStatusKey
                    if (this.chromeBorderKey) value.chrome.border_key = this.chromeBorderKey
                }
                await this.axios.post('card-mobile-settings/save', {
                    model: this.model,
                    type: 'card',
                    value,
                })
                this.$store.commit('setSnackbar', {
                    timeout: 5000,
                    text: 'Configurazione card mobile salvata',
                    value: true,
                    color: 'success',
                })
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('EntityMobileCardConfigurator: save error', err)
                const status = err.response && err.response.status
                const text = status === 403
                    ? 'Permesso negato: solo gli amministratori possono salvare questa impostazione.'
                    : 'Errore nel salvataggio della configurazione'
                this.$store.commit('setSnackbar', {
                    timeout: 6000,
                    text,
                    value: true,
                    color: 'error',
                })
            } finally {
                this.saving = false
            }
        },
    },

    mounted() {
        this.load()
    },
}
</script>

<style scoped>
/* ---------- LAYOUT ---------- */
.card-config-wrap {
    --bg-1: #fafbfc;
    --border: #e4e6eb;
    --border-2: #eef0f3;
    --text-3: #4b5563;
    --text-4: #6b7280;
    --accent: #f5b400;
}
.card-config {
    display: grid;
    grid-template-columns: minmax(340px, 400px) minmax(280px, 1fr) minmax(260px, 320px);
    gap: 16px;
    align-items: start;
}
@media (max-width: 1280px) {
    .card-config {
        grid-template-columns: minmax(320px, 380px) 1fr;
    }
    .card-config .cfg-col-available {
        grid-column: 1 / -1;
    }
    .card-config .available-column {
        position: static;
        max-height: none;
    }
}
@media (max-width: 900px) {
    .card-config { grid-template-columns: 1fr; }
}

.col-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-3);
    margin: 0 0 8px 4px;
    display: flex; align-items: center; gap: 6px;
}
.col-label .dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--accent);
    display: inline-block;
}
.col-label .dot.green { background: #10b981; }
.col-label .dot.blue { background: #3b82f6; }

/* ---------- SLOTS COLUMN ---------- */
.slots-column {
    background: #fafbfc;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px;
    display: flex; flex-direction: column; gap: 10px;
}

/* ---------- CHROME SELECTORS (chip status + border) ---------- */
.chrome-selectors {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px dashed var(--border-2);
}
.chrome-selector {
    display: flex; flex-direction: column; gap: 4px;
}
.chrome-selector-label {
    font-size: 10.5px;
    font-weight: 600;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.chrome-select {
    font-size: 12px;
}

.slot-card {
    background: #fff;
    border: 1px solid #d8dbe0;
    border-radius: 10px;
    overflow: hidden;
}
.slot-card-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 12px;
    background: #f6f7f9;
    border-bottom: 1px solid var(--border-2);
    font-size: 12px;
}
.slot-card-header .slot-name {
    font-weight: 600;
    display: inline-flex; align-items: center; gap: 6px;
}
.slot-card-header .slot-name .chip {
    background: #fff;
    border: 1px solid var(--border);
    color: var(--text-3);
    border-radius: 4px;
    padding: 1px 6px;
    font-size: 10px;
    font-family: 'JetBrains Mono', monospace;
    text-transform: uppercase;
}
.slot-card-header .slot-meta {
    font-size: 10.5px;
    color: var(--text-3);
}
.slot-list {
    padding: 8px;
    display: flex; flex-direction: column; gap: 6px;
    min-height: 46px;
    transition: background .12s;
}
.slot-list.empty {
    padding: 14px;
    align-items: center; justify-content: center;
}
.slot-empty-hint {
    color: var(--text-4);
    font-size: 11.5px;
    font-style: italic;
    text-align: center;
}
.slot-warning {
    padding: 6px 10px;
    border-top: 1px solid var(--border-2);
    background: #fffbeb;
    color: #92400e;
    font-size: 11px;
    display: flex; align-items: center; gap: 6px;
}

.field-row {
    display: flex; align-items: center; gap: 8px;
    padding: 7px 8px;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 12px;
    cursor: grab;
    user-select: none;
}
.field-row:hover {
    border-color: #d1d5db;
    box-shadow: 0 1px 2px rgba(15,23,42,.05);
}
.field-row .grip {
    color: var(--text-4);
    font-size: 14px;
    line-height: 1; cursor: grab;
    flex-shrink: 0;
}
.field-row .field-name {
    flex: 1;
    font-weight: 500;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.field-row .field-type {
    font-size: 10px;
    color: var(--text-4);
    font-family: 'JetBrains Mono', monospace;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 3px;
}
.field-row .field-actions {
    display: flex; gap: 2px;
}

/* ---------- AVAILABLE COLUMN ---------- */
.available-column {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 0;
    display: flex; flex-direction: column;
    box-shadow: 0 1px 2px rgba(15,23,42,.05);
    position: sticky; top: 12px;
    max-height: calc(100vh - 220px);
}
.available-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 14px;
    background: #f0fdf4;
    border-bottom: 1px solid #bbf7d0;
    border-radius: 12px 12px 0 0;
    gap: 8px;
}
.available-header .title { font-weight: 600; font-size: 12px; }
.available-header .count {
    font-size: 11px; color: var(--text-3);
    font-family: 'JetBrains Mono', monospace;
}
.available-search {
    padding: 8px 12px;
    border-bottom: 1px solid var(--border-2);
    position: relative;
    display: flex; align-items: center;
}
.available-search .search-icon {
    position: absolute;
    left: 22px;
    color: var(--text-4);
}
.available-search input {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 6px 10px 6px 28px;
    font-size: 12px;
    outline: none;
    background: #fff;
}
.available-search input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(245,180,0,.2);
}
.available-list {
    flex: 1; overflow-y: auto;
    padding: 8px;
    display: flex; flex-direction: column; gap: 4px;
    min-height: 200px;
}
.available-list .field-row.used {
    opacity: 0.45;
    background: #f6f7f9;
}

/* ---------- PREVIEW (Vuetify-based) ---------- */
.preview-pane {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(15,23,42,.05);
    padding: 18px;
    position: sticky; top: 12px;
}
.preview-pane-title {
    font-size: 13px; font-weight: 600;
    margin-bottom: 4px;
}
.preview-sub {
    font-size: 11px; color: var(--text-3);
    margin-bottom: 14px;
}
.preview-stage {
    background:
        linear-gradient(0deg, #f8f9fb, #f8f9fb),
        repeating-linear-gradient(45deg, transparent 0, transparent 10px, rgba(0,0,0,.015) 10px, rgba(0,0,0,.015) 11px);
    border: 1px dashed var(--border);
    border-radius: 14px;
    padding: 22px 18px;
    display: flex; flex-direction: column; align-items: stretch;
}
/* --- Card mock (Vuetify v-card + bordo sinistro) --- */
.preview-m-card {
    display: flex !important;
    overflow: hidden;
    border-radius: 10px !important;
}
/* Bordo sinistro colorato statico (azzurro informativo — la logica reale è nel wrapper mobile) */
.preview-color-bar {
    width: 5px;
    flex-shrink: 0;
    background: #3b82f6;
    align-self: stretch;
}
/* Riquadro foto placeholder (solo entità con media.photo, es. product).
   NON renderizza la foto reale: comunica dove andrà l'unica foto su mobile. */
.preview-photo-ph {
    flex-shrink: 0;
    width: 54px;
    align-self: stretch;
    background: #f3f4f6;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
}
.preview-photo-ph-label {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9ca3af;
}
.preview-card-inner {
    flex: 1;
    padding: 10px 12px;
    display: flex; flex-direction: column; gap: 6px;
}

/* Header chrome */
.preview-header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 6px;
    margin-bottom: 4px;
    min-width: 0;
    width: 100%;
}
/* Lato sinistro: si comprime per fare spazio alla data (priorità data > chip status) */
.preview-header-left {
    display: flex; align-items: center; gap: 4px; flex-wrap: nowrap;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
}
/* Lato destro: NON si comprime mai — la data resta sempre intera */
.preview-header-right {
    font-size: 11.5px;
    color: #6b7280;
    flex: 0 0 auto;
    white-space: nowrap;
}
.preview-chip-mono {
    font-family: 'JetBrains Mono', monospace !important;
    flex-shrink: 0; /* identifier (#13) sempre intero */
}
/* Status chip: si comprime con ellipsis se lo spazio finisce */
.preview-status-chip {
    flex-shrink: 1;
    min-width: 0;
}
.preview-status-chip >>> .v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    display: block;
}
.preview-meta-right {
    display: inline-flex; align-items: center;
    font-size: 11px; color: #6b7280;
    white-space: nowrap;
}

/* ---------- BODY: hero + griglia (mirror di EntityListCard mobile) ----------
   L'hero non ha label: titolo, entità collegata e assegnatari si riconoscono
   dalla forma. I campi secondari stanno in celle a fondo tenue con micro-label
   uppercase. Misure allineate ai token --gmh-card-* del mobile. */
.preview-body {
    display: flex; flex-direction: column;
}
.preview-body-empty {
    padding: 8px 0;
}

.preview-title {
    margin-top: 7px;
    font-size: 16px; font-weight: 700; line-height: 1.25;
    color: rgba(0, 0, 0, 0.87);
    word-break: break-word;
}

.preview-entity {
    display: flex; align-items: center; gap: 8px;
    margin-top: 7px;
    min-width: 0;
}
.preview-entity-mono {
    flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    width: 26px; height: 26px;
    border-radius: 7px;
    background: rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.87);
    font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
}
.preview-entity-text {
    display: flex; flex-direction: column;
    min-width: 0;
}
.preview-entity-name {
    font-size: 13.5px; font-weight: 600; line-height: 1.25;
    color: rgba(0, 0, 0, 0.87);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.preview-entity-caption {
    font-size: 12px; line-height: 1.3;
    color: #6b7280;
}

.preview-assignees {
    display: flex; align-items: center; gap: 6px;
    margin-top: 7px;
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.87);
}
.preview-assignees-value {
    min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* Niente riquadro sulle celle: il fondo tenue costava 16px di padding verticale
   per riga senza aggiungere informazione (v. EntityListCard mobile). */
.preview-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 14px;
    margin-top: 8px;
}
.preview-cell {
    display: flex; flex-direction: column;
    min-width: 0;
}
.preview-cell--wide {
    grid-column: span 2;
}
.preview-cell-label {
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.6px; text-transform: uppercase;
    color: #6b7280;
}
.preview-cell-value {
    font-size: 13px; line-height: 1.3;
    color: rgba(0, 0, 0, 0.87);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
}

/* Le label del pool campi (colonna destra) restano righe label→valore. */
.meta-label {
    color: #6b7280;
    font-weight: 500;
    margin-right: 3px;
}

/* Espansione preview */
.preview-expansion {
    border-top: 1px dashed #e5e7eb;
    padding-top: 6px;
    margin-top: 2px;
}
.preview-expanded-content {
    margin-top: 6px;
}
.preview-chevron-open {
    transform: rotate(180deg);
    transition: transform 0.18s ease;
}

/* ---------- DRAG GHOSTS ---------- */
.ghost-element {
    background: #BBDEFB !important;
    opacity: 0.5;
}
.chosen-element {
    background: #BBDEFB !important;
    opacity: 0.7;
}
</style>
